import React, { useState, useEffect } from 'react';
import { notification, Table, Space, Button, Upload, message, Card, Empty } from 'antd';
import { FileTextOutlined, ReloadOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { getInstrumentFiles, uploadInstrumentFile } from '../services/instrument.service.ts';

const { Dragger } = Upload;

const InstrumentFileUpload = ({ plantId, instrumentId }) => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notificationApi, contextHolder] = notification.useNotification();

  useEffect(() => {
    if (plantId && instrumentId) {
      loadFiles();
    }
  }, [plantId, instrumentId]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const data = await getInstrumentFiles(instance, plantId, instrumentId);
      setFiles(data || []);
    } catch (error) {
      notificationApi.error({
        message: 'Erro',
        description: 'Falha ao carregar arquivos',
        duration: 3,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    try {
      setUploading(true);
      
      await uploadInstrumentFile(instance, plantId, instrumentId, file);
      
      notificationApi.success({
        message: 'Sucesso',
        description: `Arquivo ${file.name} enviado com sucesso`,
        duration: 3,
      });
      
      // Recarregar a lista de arquivos
      await loadFiles();
      
      return false; // Impede o upload automático do componente Upload
    } catch (error) {
      notificationApi.error({
        message: 'Erro',
        description: `Falha ao enviar arquivo ${file.name}`,
        duration: 3,
      });
      return false;
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      title: 'Nome do Arquivo',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <FileTextOutlined />
          <span>{text || record.filename}</span>
        </Space>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => {
              if (record.url) {
                window.open(record.url, '_blank');
              }
            }}
          >
            Visualizar
          </Button>
        </Space>
      ),
      width: 120,
    },
  ];

  const uploadProps = {
    name: 'file',
    multiple: true,
    beforeUpload: handleUpload,
    showUploadList: false,
    accept: '*/*',
  };

  return (
    <div style={{ padding: '20px' }}>
      {contextHolder}
      
      <Card
        title="Upload de Documentos"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={loadFiles}
            loading={loading}
          >
            Atualizar
          </Button>
        }
        style={{ marginBottom: '20px' }}
      >
        <Dragger {...uploadProps} disabled={uploading}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
          </p>
          <p className="ant-upload-text">
            Clique ou arraste arquivos para esta área para fazer upload
          </p>
          <p className="ant-upload-hint">
            Suporte para upload único ou em lote. Todos os tipos de arquivo são aceitos.
          </p>
        </Dragger>
        
        {uploading && (
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <span>Enviando arquivo...</span>
          </div>
        )}
      </Card>

      <Card title="Arquivos Enviados">
        {files.length > 0 ? (
          <Table
            columns={columns}
            dataSource={files}
            rowKey={(record) => record.id || record._id || record.name}
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total de ${total} arquivos`,
            }}
          />
        ) : (
          <Empty
            description="Nenhum arquivo enviado ainda"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>
    </div>
  );
};

export default InstrumentFileUpload;